import { ref, inject, computed } from "vue";
import Toast from "@/plugins/Toast";
import { ToastType } from "@/types/toast";

export default class CreditSimulatorViewModel {
  public dialog = ref(false);
  public amount = ref<number | null>(null);
  public paymentDate = ref<string | null>(this.getLocalDateString(new Date()));
  public paymentDateMenu = ref(false);
  public installments = ref<number | null>(null);
  public rate = ref<number | null>(null);
  public monthlyPayment = ref<number | null>(null);
  public toast: Toast;

  public amountRules: Array<(value: number) => boolean | string>;
  public installmentRules: Array<(value: number) => boolean | string>;
  public rateRules: Array<(value: number) => boolean | string>;

  public installmentOptions = [1, 3, 6, 12, 24, 36, 48, 60];

  public formattedPaymentDate = computed((): string => {
    if (this.paymentDate.value) {
      const [year, month, day] = this.paymentDate.value.split("-"); // Split the `YYYY-MM-DD`
      return `${day}/${month}/${year}`; // Return as DD/MM/YYYY
    }
    return "";
  });
  private getLocalDateString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`; // Format as YYYY-MM-DD
  }
  constructor() {
    this.toast = inject("toast") as Toast;

    // Validation rules
    this.amountRules = [
      (v) => !!v || "El monto es obligatorio.",
      (v) => v > 0 || "El monto debe ser mayor que 0.",
    ];
    this.installmentRules = [
      (v) => !!v || "Debe seleccionar el número de cuotas.",
      (v) =>
        this.installmentOptions.includes(v) || "Número de cuotas no válido.",
    ];
    this.rateRules = [
      (v) => !!v || "La tasa de interés es obligatoria.",
      (v) => v > 0 || "La tasa debe ser mayor que 0.",
    ];
  }

  public get isFormValid(): boolean {
    return (
      this.amount.value !== null &&
      this.amount.value > 0 &&
      this.installments.value !== null &&
      this.installmentOptions.includes(this.installments.value) &&
      this.rate.value !== null &&
      this.rate.value > 0
    );
  }

  public openDialog(): void {
    this.resetForm();
    this.dialog.value = true;
  }

  public closeDialog(): void {
    this.dialog.value = false;
  }

  public calculateSimulation(): void {
    if (!this.isFormValid) {
      this.toast.showToast(
        "Por favor, complete todos los campos correctamente.",
        ToastType.Warning
      );
      return;
    }

    const principal = this.amount.value as number;
    const months = this.installments.value as number;
    const interestRate = (this.rate.value as number) / 100 / 12;

    const numerator = principal * interestRate;
    const denominator = 1 - Math.pow(1 + interestRate, -months);

    this.monthlyPayment.value = numerator / denominator;
    this.toast.showToast(
      "Simulación completada exitosamente.",
      ToastType.Success
    );
  }

  public closeMenu(): void {
    this.paymentDateMenu.value = false;
  }

  private resetForm(): void {
    this.amount.value = null;
    this.paymentDate.value = new Date().toISOString().slice(0, 10); // Reset to today
    this.installments.value = null;
    this.rate.value = null;
    this.monthlyPayment.value = null;
  }
}
